import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { ZoomableImage } from '@/components/common/ImageLightbox';
import { useImageLightbox } from '@/components/common/ImageLightboxContext';
import { MotionReveal } from '@/components/common/MotionReveal';

const easeOutQuint = [0.22, 1, 0.36, 1] as const;
const storyImageId = 'about-story-image';

const storySteps = [
  {
    shortTitle: 'Razumevanje',
    title: 'Razumemo šta treba da bude završeno.',
    text: 'Na prvom razgovoru definišemo rezultat, učesnike i probleme koji trenutno usporavaju posao. Bez toga ne predlažemo rešenje.',
    image:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=86',
    imageAlt: 'Prostor za prvi razgovor i definisanje zadatka'
  },
  {
    shortTitle: 'Plan',
    title: 'Cilj pretvaramo u realan plan rada.',
    text: 'Zadatke raspoređujemo prema odgovornosti, roku i međusobnim zavisnostima. Tim dobija pregled koji može da koristi svakog dana.',
    image:
      'https://images.unsplash.com/photo-1497366412874-3415097a27e7?auto=format&fit=crop&w=1600&q=86',
    imageAlt: 'Radni prostor za pripremu i praćenje plana'
  },
  {
    shortTitle: 'Realizacija',
    title: 'Pratimo obaveze dok posao nije završen.',
    text: 'Vodimo status, beležimo odluke i prijavljujemo odstupanja na vreme. Klijent zna šta je urađeno, šta kasni i ko preuzima sledeći korak.',
    image:
      'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1600&q=86',
    imageAlt: 'Završen projekat nakon sprovedenog plana'
  }
];

export function AboutPreviewSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [cycleId, setCycleId] = useState(0);
  const [stepInteractionActive, setStepInteractionActive] = useState(false);
  const reduceMotion = useReducedMotion();
  const { activeImageId } = useImageLightbox();
  const activeStep = storySteps[activeIndex];
  const timerPaused = stepInteractionActive || activeImageId === storyImageId;

  const selectStep = (index: number) => {
    setActiveIndex(index);
    setCycleId(current => current + 1);
  };

  return (
    <section className="border-y border-primary/12 bg-primary-soft py-18 sm:py-22 lg:py-27">
      <div className="site-container">
        <MotionReveal className="grid gap-5 lg:grid-cols-[0.82fr_0.48fr] lg:items-end lg:gap-24">
          <div>
            <p className="section-label">Kako izgleda saradnja</p>
            <h2 className="mt-3 max-w-4xl text-balance text-3xl font-semibold leading-[1.1] tracking-[-0.045em] text-text-primary sm:text-5xl lg:text-[3.35rem]">
              Od prvog razgovora do završenog zadatka.
            </h2>
          </div>
          <p className="max-w-lg text-base leading-7 text-text-secondary lg:pb-1">
            Tri faze drže projekat preglednim i daju timu jasan sledeći korak.
          </p>
        </MotionReveal>

        <div className="mt-10 grid lg:mt-14 lg:grid-cols-12 lg:items-start">
          <div className="overflow-hidden lg:col-span-8 lg:col-start-1 lg:row-start-1">
            <AnimatePresence mode="wait" initial={false}>
              <motion.figure
                key={activeStep.shortTitle}
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, clipPath: 'inset(0 10% 0 0)' }
                }
                animate={{ opacity: 1, clipPath: 'inset(0 0% 0 0)' }}
                exit={reduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: 0.38, ease: easeOutQuint }}
              >
                <ZoomableImage
                  lightboxId={storyImageId}
                  className="aspect-[16/10] min-h-[300px] w-full rounded-[var(--radius-md)] object-cover sm:min-h-0"
                  src={activeStep.image}
                  alt={activeStep.imageAlt}
                  sizes="(min-width: 1024px) 62vw, (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)"
                  loading="lazy"
                />
              </motion.figure>
            </AnimatePresence>
          </div>

          <div
            className="relative z-10 -mt-5 bg-surface p-6 shadow-[0_12px_30px_rgba(20,31,23,0.1)] sm:p-8 lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:mt-14 lg:p-9 dark:shadow-none"
            onPointerEnter={() => setStepInteractionActive(true)}
            onPointerLeave={() => setStepInteractionActive(false)}
            onFocusCapture={() => setStepInteractionActive(true)}
            onBlurCapture={event => {
              const nextTarget = event.relatedTarget;
              if (
                !(nextTarget instanceof Node) ||
                !event.currentTarget.contains(nextTarget)
              ) {
                setStepInteractionActive(false);
              }
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeStep.title}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -6 }}
                transition={{ duration: 0.26, ease: easeOutQuint }}
              >
                <p className="text-sm font-semibold text-primary">
                  Korak 0{activeIndex + 1}
                </p>
                <h3 className="mt-3 text-balance text-2xl font-semibold leading-[1.2] tracking-[-0.035em] text-text-primary sm:text-3xl">
                  {activeStep.title}
                </h3>
                <p className="mt-4 text-sm leading-6 text-text-secondary sm:text-base sm:leading-7">
                  {activeStep.text}
                </p>
              </motion.div>
            </AnimatePresence>
            <Button className="mt-7" to="/o-nama" variant="outline">
              Više o našem pristupu
              <ArrowRight aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>

        <div
          className="mt-7 grid grid-cols-3 border-t border-border-strong/70"
          onPointerEnter={() => setStepInteractionActive(true)}
          onPointerLeave={() => setStepInteractionActive(false)}
          onFocusCapture={() => setStepInteractionActive(true)}
          onBlurCapture={event => {
            const nextTarget = event.relatedTarget;
            if (
              !(nextTarget instanceof Node) ||
              !event.currentTarget.contains(nextTarget)
            ) {
              setStepInteractionActive(false);
            }
          }}
        >
          {storySteps.map((step, index) => (
            <button
              key={step.shortTitle}
              type="button"
              className={`animated-link relative cursor-pointer py-4 text-left text-xs font-semibold transition-colors sm:text-sm ${index === activeIndex ? 'text-primary' : 'text-text-muted hover:text-text-primary'} ${index > 0 ? 'pl-3 sm:pl-5' : ''}`}
              onClick={() => selectStep(index)}
              onFocus={() => selectStep(index)}
              aria-pressed={index === activeIndex}
            >
              <span className="mr-1.5 tabular-nums">0{index + 1}</span>
              <span>{step.shortTitle}</span>
              {index === activeIndex && (
                <span className="absolute -top-px left-0 h-0.5 w-full overflow-hidden bg-border">
                  <span
                    key={`${activeIndex}-${cycleId}`}
                    className={`story-timer-progress block h-full origin-left bg-primary ${timerPaused ? 'is-paused' : ''}`}
                    onAnimationEnd={() => {
                      if (!reduceMotion && !timerPaused) {
                        setActiveIndex(
                          current => (current + 1) % storySteps.length
                        );
                      }
                    }}
                  />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
